import { el, clear } from '../lib/dom.js';
import { getMove, getContestEffect, getSuperContestEffect } from '../data/api.js';
import { loadSnapshot } from '../data/snapshot.js';
import { prettify } from '../data/pokedex-resolver.js';
import { settings } from '../store/settings.js';
import { machineNumber } from '../components/MovesList.js';
import { Sprite } from '../components/Sprite.js';
import { TypeChip } from '../components/TypeChip.js';
import { typeHex } from '../lib/type-color.js';

const DMG = { physical: 'Physical', special: 'Special', status: 'Status' };
const idFromUrl = (u) => Number(u.replace(/\/$/, '').split('/').pop());

const english = (entries, key) => {
  const hit = (entries || []).find((e) => e.language.name === 'en');
  return hit ? hit[key].replace(/\s+/g, ' ').trim() : '';
};

function fact(k, v) {
  return el('div', { class: 'fact' }, el('dt', {}, k), el('dd', {}, String(v)));
}

export async function MoveDetail(id) {
  const view = el('section', { class: 'view lookup' });
  view.append(el('div', { class: 'view--loading' }, 'Loading move…'));

  const snap = await loadSnapshot();

  let d;
  try {
    d = await getMove(id);
  } catch (err) {
    clear(view).append(
      el(
        'div',
        { class: 'view--error' },
        el('h1', {}, 'Could not load this move'),
        el('p', {}, navigator.onLine ? err.message : 'You appear to be offline.'),
        el('a', { class: 'button', href: '#/search' }, 'Back to lookup'),
      ),
    );
    return view;
  }

  const type = d.type.name;
  view.style.setProperty('--type-main', typeHex(type));

  const chance = d.effect_chance;
  let effect =
    english(d.effect_entries, 'short_effect') ||
    english(d.flavor_text_entries, 'flavor_text') ||
    'No description.';
  if (chance != null) effect = effect.replace(/\$effect_chance%?/g, `${chance}%`);

  const bits = [];
  if (d.priority) bits.push(`Priority ${d.priority > 0 ? '+' : ''}${d.priority}`);
  if (d.target?.name) bits.push(`Target: ${prettify(d.target.name)}`);
  if (d.meta?.ailment?.name && d.meta.ailment.name !== 'none') {
    bits.push(
      `May ${prettify(d.meta.ailment.name)}${d.meta.ailment_chance ? ` (${d.meta.ailment_chance}%)` : ''}`,
    );
  }
  if (d.meta?.drain)
    bits.push(`${d.meta.drain > 0 ? 'Drains' : 'Recoil'} ${Math.abs(d.meta.drain)}%`);
  if (d.meta?.healing) bits.push(`Heals ${d.meta.healing}%`);
  if (d.meta?.crit_rate) bits.push(`+${d.meta.crit_rate} crit rate`);
  if (d.meta?.flinch_chance) bits.push(`Flinch ${d.meta.flinch_chance}%`);
  const statChanges = (d.stat_changes || [])
    .map((s) => `${s.change > 0 ? '+' : ''}${s.change} ${prettify(s.stat.name)}`)
    .join(', ');
  if (statChanges) bits.push(statChanges);

  // Contest stats (Ruby/Sapphire-era Contests, carried through to ORAS's
  // Contest Spectaculars) — only moves old enough to have them carry a
  // contest_type; appeal/jam live on two further resources, fetched only
  // when there's something to show.
  const contestType = d.contest_type?.name || '';
  const [contestFx, superContestFx] = await Promise.all([
    d.contest_effect?.url
      ? getContestEffect(idFromUrl(d.contest_effect.url)).catch(() => null)
      : null,
    d.super_contest_effect?.url
      ? getSuperContestEffect(idFromUrl(d.super_contest_effect.url)).catch(() => null)
      : null,
  ]);

  // Pokémon that can learn this move. PokéAPI only gives a flat, all-games
  // list — narrow it to species that existed by the selected game's
  // generation so a "Crystal" view isn't full of Gen 9 Pokémon.
  const vg = settings.get().versionGroup;
  const gen = snap.versionGroupByKey.get(vg)?.generation ?? 9;
  let learners = (d.learned_by_pokemon || [])
    .map((p) => snap.speciesById.get(idFromUrl(p.url)))
    .filter(Boolean);
  if (vg !== 'all') learners = learners.filter((sp) => sp.generation <= gen);
  learners.sort((a, b) => a.id - b.id);

  const facts = el(
    'dl',
    { class: 'pfacts' },
    fact('Power', d.power ?? '—'),
    fact('Accuracy', d.accuracy != null ? `${d.accuracy}%` : '—'),
    fact('PP', d.pp ?? '—'),
    fact('Priority', d.priority ?? 0),
    fact('Introduced', prettify(d.generation.name)),
    fact('Learned by', `${learners.length} Pokémon`),
  );
  if (d.machines?.length) {
    const vg = settings.get().versionGroup;
    const machineFact = fact('TM / HM', '…');
    facts.append(machineFact);
    const exact = d.machines.some((x) => x.version_group.name === vg);
    machineNumber(d, vg).then((label) => {
      machineFact.querySelector('dd').textContent = label
        ? exact
          ? label
          : `${label} (latest)`
        : '—';
    });
  }

  // ---- Learners grid (filterable) ----------------------------------
  const style = settings.get().spriteStyle;
  const LEARNER_CAP = 400;
  const learnersGrid = el('div', { class: 'ab-mons' });
  function paintLearners(q) {
    const qq = (q || '').trim().toLowerCase().replace(/\s+/g, '-');
    const shown = qq ? learners.filter((sp) => sp.name.includes(qq)) : learners;
    clear(learnersGrid);
    if (!shown.length) {
      learnersGrid.append(el('p', { class: 'detail__muted' }, 'No matches.'));
      return;
    }
    for (const sp of shown.slice(0, LEARNER_CAP)) {
      learnersGrid.append(
        el(
          'a',
          { class: 'ab-mon', href: `#/pokemon/${sp.id}` },
          Sprite(sp.id, { style, alt: sp.name, size: 48 }),
          el('span', {}, sp.name.replace(/-/g, ' ')),
        ),
      );
    }
    if (shown.length > LEARNER_CAP) {
      learnersGrid.append(
        el(
          'p',
          { class: 'detail__muted' },
          `+${shown.length - LEARNER_CAP} more — filter to narrow.`,
        ),
      );
    }
  }
  paintLearners('');
  const learnerFilter = el('input', {
    type: 'search',
    class: 'search__input learners__filter',
    placeholder: 'Filter Pokémon…',
    oninput: (e) => paintLearners(e.target.value),
  });

  const contestSection = contestType
    ? el(
        'section',
        { class: 'detail__section' },
        el('h2', {}, 'Contest'),
        el(
          'dl',
          { class: 'pfacts' },
          fact('Category', prettify(contestType)),
          contestFx ? fact('Appeal', contestFx.appeal) : null,
          contestFx?.jam ? fact('Jam', contestFx.jam) : null,
          superContestFx ? fact('Super Contest appeal', superContestFx.appeal) : null,
        ),
        contestFx?.effect_entries?.length
          ? el('p', { class: 'detail__muted' }, english(contestFx.effect_entries, 'effect'))
          : null,
      )
    : null;

  const learnersSection = learners.length
    ? el(
        'section',
        { class: 'detail__section' },
        el(
          'h2',
          {},
          `Pokémon that can learn this${vg === 'all' ? '' : ` in ${prettify(vg)}`} (${learners.length})`,
        ),
        learners.length > 24 ? learnerFilter : null,
        learnersGrid,
      )
    : null;

  clear(view).append(
    el('nav', { class: 'lookup__nav' }, el('a', { class: 'link', href: '#/search' }, '‹ Lookup')),
    el(
      'header',
      { class: 'lookup__head', dataset: { type } },
      el('h1', {}, d.name.replace(/-/g, ' ')),
      el(
        'div',
        { class: 'lookup__chips' },
        TypeChip(type),
        el('span', { class: 'lookup__class' }, DMG[d.damage_class?.name] || '—'),
      ),
    ),
    facts,
    el(
      'section',
      { class: 'detail__section' },
      el('h2', {}, 'Effect'),
      el('p', {}, effect),
      bits.length ? el('p', { class: 'movebody__bits' }, bits.join(' · ')) : null,
    ),
    // Spread, not a bare arg: clear(view).append(...) is the native
    // Element.append(), which stringifies a null section into a literal
    // "null" text node instead of skipping it like el() would.
    ...(contestSection ? [contestSection] : []),
    ...(learnersSection ? [learnersSection] : []),
  );
  return view;
}
